import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { CITIES } from "../lib/data/cities";
import { Reveal, RevealLines } from "../components/motion/Reveal";
import EditorialMarquee from "../components/site/EditorialMarquee";
import { setSEO } from "../lib/data/seo";

export default function Cities() {
  const [hovered, setHovered] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setSEO({
      title: "Security Services in British Columbia — 50+ Cities Covered",
      description:
        "Local security guards, mobile patrol, and specialty services across 50+ British Columbia cities including Vancouver, Victoria, Kelowna, Prince George, and more.",
      keywords: CITIES.flatMap((c) => c.keywords || []),
      canonical: `${window.location.origin}/cities`,
    });
  }, []);

  const grouped = useMemo(() => {
    const filter = query.trim().toLowerCase();
    const filtered = CITIES.filter(
      (c) =>
        !filter ||
        c.name.toLowerCase().includes(filter) ||
        (c.region || "").toLowerCase().includes(filter)
    );
    const g = {};
    filtered.forEach((c) => {
      const letter = c.name[0].toUpperCase();
      g[letter] = g[letter] || [];
      g[letter].push(c);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [query]);

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="pt-16 md:pt-24 pb-16 bg-[#F4F4F0]" data-testid="cities-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            Directory · Cities · 50+
          </div>
          <RevealLines
            lines={["Every city.", "Every corner.", "One operator."]}
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.9] text-[#060913]"
          />
          <p className="mt-10 max-w-2xl text-lg md:text-xl text-[#5C5F66] leading-relaxed">
            Metro Vancouver, Vancouver Island, the Fraser Valley, the Okanagan,
            the Kootenays, the North Coast, the Peace Region — dispatched under
            one provincial contract.
          </p>

          <div className="mt-12 max-w-md">
            <label className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Filter cities
            </label>
            <input
              data-testid="cities-filter"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or region…"
              className="mt-2 w-full bg-transparent border-b border-black/40 py-3 text-lg outline-none focus:border-[#D92323]"
            />
          </div>
        </div>
      </section>

      <EditorialMarquee items={["METRO VANCOUVER", "VANCOUVER ISLAND", "OKANAGAN", "FRASER VALLEY", "KOOTENAYS", "NORTH COAST", "PEACE REGION"]} />

      {/* Alphabetical index */}
      <section className="bg-[#F4F4F0] pb-32" data-testid="cities-index">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          {grouped.map(([letter, list]) => (
            <div key={letter} className="grid grid-cols-12 gap-6 border-t border-black/10 py-10">
              <div className="col-span-2 md:col-span-1">
                <div className="font-display text-6xl md:text-7xl text-[#D92323] leading-none">
                  {letter}
                </div>
              </div>
              <div className="col-span-10 md:col-span-11">
                <ul>
                  {list.map((c, i) => (
                    <Reveal key={c.slug} delay={i * 0.02}>
                      <li
                        onMouseEnter={() => setHovered(c)}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <Link
                          to={`/cities/${c.slug}`}
                          data-testid={`city-link-${c.slug}`}
                          className="group grid grid-cols-12 gap-4 items-baseline py-4 border-b border-black/10 hover:bg-white transition-colors -mx-4 px-4"
                        >
                          <span className="col-span-8 md:col-span-6 font-display text-3xl md:text-5xl tracking-tighter leading-[0.95] text-[#060913] group-hover:text-[#D92323] transition-colors">
                            {c.name}
                          </span>
                          <span className="hidden md:block col-span-4 font-mono text-[11px] tracking-[0.22em] uppercase text-[#5C5F66]">
                            {c.region}
                          </span>
                          <span className="col-span-4 md:col-span-2 text-right font-mono text-[11px] tracking-[0.22em] uppercase text-black/50 group-hover:text-[#D92323]">
                            →
                          </span>
                        </Link>
                      </li>
                    </Reveal>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Cursor-attached preview */}
        {hovered && (
          <div className="hidden md:block fixed pointer-events-none top-1/2 right-10 -translate-y-1/2 w-64 h-80 z-40 overflow-hidden shadow-2xl">
            <img src={hovered.image} alt={hovered.name} className="w-full h-full object-cover" />
            <div className="absolute bottom-2 left-2 font-mono text-[10px] tracking-[0.22em] uppercase text-white/95 mix-blend-difference">
              {hovered.name} · {hovered.region}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
